import { Icon, IconName } from '@usealloy/component-library';
import { useContext } from 'react';
import { useLocation } from 'react-router-dom';

import { RULE_LIBRARY_ORGANIZATION_SLUG } from '~/constants';
import { UserContext } from '~/dashboard/context';
import { routes } from '~/helpers/routes';

import { Sidebar } from '../component/Sidebar';
import { AdminGroup } from './NavList.admin';
import { BuildGroup } from './NavList.build';
import { ExploreGroup } from './NavList.explore';
import { ReviewGroup } from './NavList.review';
import { SettingsGroup } from './NavList.settings';
import { isJourneyActive } from './routeMatchers';
import { CustomerSwitcher } from './NavList.customerSwitcher';
import { AgentProfileMenuItem } from './AgentProfileMenuItem';

// ============================================================
//     TYPES
// ============================================================
export interface SideNavProps {
  /**
   * Controlled expanded state (pair with `onExpandedChange`).
   *
   * At the mount point this is wired to `useExpandingSidebar` so the expand state
   * shares the `sidebarExpanded` localStorage key (and the content-area layout class)
   * with the legacy sidebar.
   */
  expanded?: boolean;
  /**
   * Callback fired when the sidebar expand state changes.
   */
  onExpandedChange?: (expanded: boolean) => void;
}

// ============================================================
//     HELPERS
// ============================================================
/**
 * The ungrouped Home item rendered above the labeled groups (FE2.0 has no Home).
 */
const HomeGroup = () => {
  const { pathname } = useLocation();

  return (
    <Sidebar.Group>
      <Sidebar.Menu>
        <Sidebar.MenuItem>
          <Sidebar.MenuButton
            as="a"
            data-dd-action-name="alloydd-sidebar-nav-home"
            href={`${window.location.origin}${routes.v3.dashboard.home.dashboard}`}
            isActive={isJourneyActive(
              routes.v3.dashboard.home.dashboard,
              pathname,
            )}
            tooltip="Home"
          >
            <Icon icon={IconName.Home} size="small" />
            <span className="fs-unmask">Home</span>
          </Sidebar.MenuButton>
        </Sidebar.MenuItem>
      </Sidebar.Menu>
    </Sidebar.Group>
  );
};

// ============================================================
//     COMPONENT
// ============================================================
/**
 * The Sidebar_v2 navigation for the legacy dashboard.
 *
 * Backports the FE2.0 SideNav's structure (Review / Explore / Build / Settings
 * groups) onto Daffy's existing routes, permissions, and feature flags — a bridge
 * while customers transition between the legacy dashboard and the beta dashboard.
 */
export const SideNav = (props: SideNavProps) => {
  const { expanded, onExpandedChange } = props;

  const { customer } = useContext(UserContext);
  const { pathname } = useLocation();

  const inAdminDashboard = pathname.startsWith('/v3/dashboard/admin');
  const isRuleLibraryAccount = customer?.organizationNameSlug === RULE_LIBRARY_ORGANIZATION_SLUG;

  const getContent = () => {
    // Admin dashboard
    if (inAdminDashboard) {
      return (
        <Sidebar.Body>
          <AdminGroup />
        </Sidebar.Body>
      );
    }

    // Rule-library accounts only see Home and Settings
    if (isRuleLibraryAccount) {
      return (
        <>
          <HomeGroup />
          <SettingsGroup />
        </>
      );
    }

    // Default to Home and Review, Explore, Build groups
    return (
      <Sidebar.Body>
        <ReviewGroup />
        <ExploreGroup />
        <BuildGroup />
        <SettingsGroup />
      </Sidebar.Body>
    );
  };

  return (
    <Sidebar
      aria-label="Main navigation"
      expanded={expanded}
      isCollapsible
      keyboardShortcut={{ key: 'b' }}
      onExpandedChange={onExpandedChange}
      role="navigation"
      style={{ backgroundColor: 'var(--cl-gray-100)' }}
      variant="primary"
    >
      <CustomerSwitcher expanded={expanded} />
      <Sidebar.ExpandToggle data-dd-action-name="alloydd-sidebar-nav-expanded-toggle" />
      {getContent()}
      <AgentProfileMenuItem expanded={expanded} />
    </Sidebar>
  );
};
